import { useState } from "react";
import useFormContext from "../../contexts/FormContext";
import Button from "../Button";
import Seo from "../Seo";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ContactForm = () => {
  const { formData, setFormData } = useFormContext();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" })); // clear error on change
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.phone || !/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number";
    }
    if (!formData.city) {
      newErrors.city = "City is required";
    }
    if (!formData.typeofservice) {
      newErrors.typeofservice = "Type of Service is required";
    }
    if (!formData.message || formData.message.trim().length < 5) {
      newErrors.message = "Message must be at least 5 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validate()) {
    toast.error("Please fix the errors before submitting.");
    return;
  }

  setLoading(true);
  try {
    const response = await fetch("https://kk-solar-backend.onrender.com/solar-form", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    const data = await response.json();
    
    if (response.ok) {
      console.log("Response from backend:", data);
      toast.success("Message sent successfully!");
      
      // Clear form
      setFormData({
        name: "",
        email: "",
        phone: "",
        city: "",
        typeofservice: "",
        message: "",
      });
    } else {
      console.error("Backend error:", data);
      // Even if email fails, show success to user if form was processed
      if (data.error && data.error.includes("email")) {
        toast.success("Form submitted! (Email notification failed)");
      } else {
        toast.error("Failed to send message: " + (data.message || "Unknown error"));
      }
    }
  } catch (error) {
    console.error("Network error:", error);
    toast.error("Something went wrong. Please try again.");
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      <Seo
        title="Contact KK Solar | Solar Quote & Consultation in Kutch"
        description="Contact KK Solar for rooftop solar installation quotes, subsidy guidance, and solar energy solutions in Kutch and Gujarat."
        url="https://kksolar.co.in/contact"
      />
      <section className="px-4 mt-[90px] sm:px-6 md:px-16 py-12 sm:py-16 max-w-7xl mx-auto">
      <div className="w-full max-w-lg sm:max-w-3xl mx-auto p-8 sm:p-10 rounded-2xl shadow-2xl bg-white border border-gray-100 relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-opprimary/10 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-primary/10 rounded-full blur-2xl"></div>
        
        <div className="relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-6 text-center leading-tight">
            Get In <span className="text-opprimary">Touch</span>
          </h2>
          <p className="text-gray-600 text-center mb-8 text-lg">
            Please get in touch with us if you would like further details.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="group">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border-2 border-gray-200 hover:border-opprimary/50 focus:border-opprimary focus:ring-2 focus:ring-opprimary/20 px-4 py-4 rounded-xl text-base transition-all duration-300 bg-white/80 backdrop-blur-sm"
              />
              {errors.name && <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                <span>⚠️</span>
                {errors.name}
              </p>}
            </div>

            {/* Email */}
            <div className="group">
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                className="w-full border-2 border-gray-200 hover:border-opprimary/50 focus:border-opprimary focus:ring-2 focus:ring-opprimary/20 px-4 py-4 rounded-xl text-base transition-all duration-300 bg-white/80 backdrop-blur-sm"
              />
              {errors.email && <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                <span>⚠️</span>
                {errors.email}
              </p>}
            </div>

            {/* Phone */}
            <div className="group">
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                className="w-full border-2 border-gray-200 hover:border-opprimary/50 focus:border-opprimary focus:ring-2 focus:ring-opprimary/20 px-4 py-4 rounded-xl text-base transition-all duration-300 bg-white/80 backdrop-blur-sm"
              />
              {errors.phone && <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                <span>⚠️</span>
                {errors.phone}
              </p>}
            </div>

            {/* City */}
            <div className="group">
              <input
                type="text"
                name="city"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
                className="w-full border-2 border-gray-200 hover:border-opprimary/50 focus:border-opprimary focus:ring-2 focus:ring-opprimary/20 px-4 py-4 rounded-xl text-base transition-all duration-300 bg-white/80 backdrop-blur-sm"
              />
              {errors.city && <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                <span>⚠️</span>
                {errors.city}
              </p>}
            </div>

            {/* Type of Service */}
            <div className="group">
              <input
                type="text"
                name="typeofservice"
                placeholder="Type of Service"
                value={formData.typeofservice}
                onChange={handleChange}
                className="w-full border-2 border-gray-200 hover:border-opprimary/50 focus:border-opprimary focus:ring-2 focus:ring-opprimary/20 px-4 py-4 rounded-xl text-base transition-all duration-300 bg-white/80 backdrop-blur-sm"
              />
              {errors.typeofservice && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                  <span>⚠️</span>
                  {errors.typeofservice}
                </p>
              )}
            </div>

            {/* Message */}
            <div className="group">
              <textarea
                name="message"
                placeholder="Your Message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                className="w-full border-2 border-gray-200 hover:border-opprimary/50 focus:border-opprimary focus:ring-2 focus:ring-opprimary/20 px-4 py-4 rounded-xl text-base transition-all duration-300 bg-white/80 backdrop-blur-sm resize-none"
              />
              {errors.message && <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                <span>⚠️</span>
                {errors.message}
              </p>}
            </div>

            <Button 
              type="submit" 
              variant="solidorange" 
              disabled={loading}
              className="w-full py-4 text-lg font-semibold mt-4"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </span>
              ) : (
                "Send Message"
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
    </>
  );
};

export default ContactForm;
